import React, { useCallback, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Text, Tooltip } from 'rizzui';
import { ActivityChangesModalContent, formatActivityDate, renderActivityChangesSummary, renderActivityIpAddress } from '../../../../../components/activity/ActivityAuditDetail';
import AsyncImg from '../../../../../components/extras/AsyncImg';
import ModalForm from '../../../../../components/forms/ModalForm';
import { useFiltersPR } from '../../../../../components/providers/FiltersProvider';
import { FilterDrawerView } from '../../../../../components/table/components/TableFilter';
import CustomTable from '../../../../../components/table/CustomTable';
import AvatarCard from '../../../../../components/ui/AvatarCard';
import useFetch from '../../../../../hooks/useFetch';
import Page from '../../../../../layout/Page/Page';
import { menuRoutes } from '../../../../../router/menu';
import { StudentService } from '../../../../../services/student/StudentService';
import { StudentActivity, StudentActivityApiResponse } from '../../../../../type/entities/student-type';
import StudentActivityHistoryFilters from './StudentActivityHistoryFilters';

interface StudentActivityHistoryProps { }

const columnOrder = [
    'id',
    'user',
    'company',
    'actionType',
    'changes',
    'ipAddress',
    'comment',
    'createdAt',
];

const StudentActivityHistory: React.FC<StudentActivityHistoryProps> = ({ }) => {

    const { id = '' } = useParams<{ id: string }>();
    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize, resetFilters } = useFiltersPR();
    const [openFilters, setOpenFilters] = useState<boolean>(false);
    const [selectedChanges, setSelectedChanges] = useState<StudentActivity['changes']>(null);

    const [data, loading] = useFetch(useCallback(async () => {
        if (!id || id === '') return null;
        const response = await (new StudentService()).listActivityHistory({ ...filters, filter_filters: { ...filters.filter_filters, student: id } });
        return response.getResponseData() as StudentActivityApiResponse;
    }, [id, filters]));

    return (
        <Page container='fluid' className='mt-5'>
            {data && (
                <CustomTable
                    id={'student-changelogs-table'}
                    columnOrder={columnOrder}
                    data={data?.data}
                    isLoading={loading}
                    columnsNotShown={['id', 'client', 'student', 'updatedAt']}
                    overrideColumns={[
                        {
                            key: 'user',
                            render: (row: any) => {
                                const entity = row.row.original?.user;

                                if (!entity) {
                                    return <Text>N/A</Text>
                                }

                                if (!Object.prototype.hasOwnProperty.call(entity, 'profileImg')) {
                                    return (
                                        <AvatarCard
                                            src={'default-avatar.png'}
                                            name={entity.name + (entity.lastName ? ` ${entity.lastName}` : '')}
                                            link={`${menuRoutes.users.path}/${entity.id}/profile${menuRoutes.users.profile.info}`}
                                        />
                                    )
                                }

                                return (
                                    <Link to={`/users/${entity.id}/edit`}>
                                        <div className="flex items-center gap-3">
                                            <AsyncImg id={entity.id} className="mx-auto d-block rounded w-[40px] h-[40px] object-cover" />
                                            <Text>{entity.name + (entity.lastName ? ` ${entity.lastName}` : '')}</Text>
                                        </div>
                                    </Link>
                                )
                            },
                        },
                        {
                            key: 'company',
                            render: (row: any) => {
                                const entity = (row.row.original as StudentActivity).company;
                                return (
                                    <Link to={`/coorporation/${entity.id}/view`}>
                                        <div className="flex items-center gap-3">
                                            <Text>{entity.name}</Text>
                                        </div>
                                    </Link>
                                )
                            },
                        },
                        {
                            key: 'actionType',
                            render: (row: any) => <Text>{(row.row.original as StudentActivity).actionType || 'Registro legado'}</Text>,
                        },
                        {
                            key: 'changes',
                            render: (row: any) => renderActivityChangesSummary(
                                (row.row.original as StudentActivity).changes,
                                (changes) => setSelectedChanges(changes)
                            ),
                        },
                        {
                            key: 'ipAddress',
                            render: (row: any) => renderActivityIpAddress(row.row.original as StudentActivity),
                        },
                        {
                            key: 'comment',
                            render: (row: any) => {
                                const comment = (row.row.original as StudentActivity).comment;
                                return (
                                    <Tooltip
                                        size="md"
                                        content={<div className='tooltip-container'>{comment}</div>}
                                        placement="top"
                                        color="invert"
                                    >
                                        <div>{comment.substring(0, 100)} {comment.length > 100 && '...'}</div>
                                    </Tooltip>)
                            }
                        },
                        {
                            key: 'createdAt',
                            render: (row: any) => <Text>{formatActivityDate((row.row.original as StudentActivity).createdAt)}</Text>,
                        }
                    ]}
                    filters={filters}
                    updateFilters={updateFilters}
                    updateFilterOrder={updateFilterOrder}
                    defaultOrder={filters.filter_order || undefined}
                    paginationData={{
                        pageSize: filters.limit,
                        currentPage: filters.page,
                        pageCount: data ? data.lastPage : 1,
                        totalCount: data?.totalRegisters,
                        handlePagination: updatePage,
                        handlePerPage: updatePageSize,
                    }}
                    toggleFilters={() => setOpenFilters(!openFilters)}
                />
            )}

            <FilterDrawerView isOpen={openFilters} setOpenDrawer={setOpenFilters} drawerTitle={'Filtros Actividad'}>
                <StudentActivityHistoryFilters filters={filters} updateFilters={updateFilters} resetFilters={resetFilters} />
            </FilterDrawerView>
            <ModalForm
                isOpen={selectedChanges !== null}
                onClose={() => setSelectedChanges(null)}
                title='Detalle de cambios'
                size='xl'
            >
                <ActivityChangesModalContent changes={selectedChanges} />
            </ModalForm>
        </Page>
    );
};

export default StudentActivityHistory;
